import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { storeLogin } from "~/api/store/requests";
import { ILoginResponse } from "~/api/account/types";
import { paths } from "~/router/paths";
import { setJWT, setRefreshToken } from "~/helpers/auth";
import { useAccount } from "~/hooks/useAccount";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "~/components/toast";

export const useStoreLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAccount } = useAccount();
  const client = useQueryClient();

  return useCallback(
    async (storeToken: string) => {
      const params = {
        shop_token: storeToken,
      };

      client.clear();
      await setJWT("");
      await setRefreshToken("");
      try {
        const res = (await storeLogin(params)) as ILoginResponse;
        await setJWT(res.access);
        await setRefreshToken(res.refresh);
        setAccount(res.user);
        navigate(paths.app.home, { replace: true });
      } catch (e: any) {
        if (e?.response?.status === 429) {
          const retryAfter = e.response?.headers?.["retry-after"];
          if (retryAfter) {
            const minutes = Math.ceil(Number(retryAfter) / 60);
            toast.error(t("auth.too_many_requests_retry", { minutes }));
          } else {
            toast.error(t("auth.too_many_requests"));
          }
        } else {
          console.error(e);
        }
      }
    },
    [client, navigate, setAccount, t]
  );
};
